import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDidMount, useDidUpdate } from 'rooks';
import { useDispatch, useSelector } from 'react-redux';

import {
  DESTINATION_GROUP,
  NAVIGATION_ORIGIN,
  NAVIGATION_TRIGGER_TYPE,
} from 'root/lib/SearchlightDispatcher';
import STEP_TYPE from 'root/activities/hub/data/StepTypes';
import GameIds from 'a/hub/data/GameIds';
import { reportNavigation } from 'a/hub/actions/metagameSearchlight';
import { openEreader } from 'a/eReader/redux/actions/ereader';
import {
  getGameId,
  getGameName,
  findChapterName,
} from 'lib/utilityFunctions';
import createActionObj from '../../../../../../actions/createActionObj';
import ModalLargeBackground from './svgs/BgWide.svg';
import OliviaIcon from './svgs/Olivia.svg';
import GameIcon from '../../games/GameIcon';
import {
  getIsChallengeQuestComplete,
  getChallengeQuestGames,
  getAreAllQuestsAndChallengesComplete,
  getShouldStartNewChallengeQuest,
} from '../../../../../selectors/workshopSelectors';
import getGamesForChallengeQuest from '../../../../../../api/challengeQuest';
import { updateChallengeQuestGames } from '../../../../../actions/workshopActions';
import goToActivity from '../../../../../../actions/navigationActions';

import { openEndOfAllQuestsAndChallengesModal } from '../../../../../actions/modalSystemActions';
import useWorkshopSfx from '../../../hooks/useWorkshopSfx';
import {
  Title,
  BackgroundContainer,
  CloseModalButton,
  Description,
  DescriptionContainer,
  GamesContainer,
  IconContainer,
  TitleContainer,
  GoButton,
  Spinner,
  StyledIconWrapper,
} from './styledComponents';
import { setGameLaunchedFrom } from '../../../../../actions/metaGameActions';
import { ModalContainer } from '../sharedComponents';

const selectLessMasteredSkillId = (games) => {
  const found = games.find(
    game => game.levelsMastered.length < game.levels.length,
  );
  return found ? found.skillId : 0;
};

const ChallengeQuestModal = ({ closeModal }) => {
  const dispatch = useDispatch();
  const games = useSelector(getChallengeQuestGames);
  const isChallengeQuestComplete = useSelector(getIsChallengeQuestComplete);
  const resetGames = useSelector(getShouldStartNewChallengeQuest);
  const areAllQuestsAndChallengesComplete = useSelector(
    getAreAllQuestsAndChallengesComplete,
  );

  const [gameSelectedSkillId, setGameSelectedSkillId] = useState(
    selectLessMasteredSkillId(games),
  );
  const [isLoaded, setLoaded] = useState(
    !resetGames && !isChallengeQuestComplete && games.length > 0,
  );

  const { playTap, playSfxFromMap, audioMap, globalSfx } = useWorkshopSfx();

  const clickedRef = useRef(false);
  const timeout = useRef(null);
  useEffect(() => {
    playSfxFromMap(audioMap.skill_game_launch);
    return () => {
      clearTimeout(timeout.current);
    };
  }, []);

  useDidUpdate(() => {
    if (isLoaded) {
      globalSfx.pop();
    }
  }, [isLoaded]);

  useDidMount(async () => {
    if (areAllQuestsAndChallengesComplete) {
      closeModal();
      dispatch(openEndOfAllQuestsAndChallengesModal());
    } else if (!isLoaded) {
      const challengeGames = await dispatch(getGamesForChallengeQuest());
      // only keep games of type GAME (not EREADER)
      const filteredGames = challengeGames.filter(game => game.type === STEP_TYPE.GAME);
      if (filteredGames.length) {
        setLoaded(true);
        setGameSelectedSkillId(selectLessMasteredSkillId(filteredGames));
      } else {
        // no games left after fetching, open end of all quests and challenges modal
        closeModal();
        dispatch(openEndOfAllQuestsAndChallengesModal());
      }
      dispatch(updateChallengeQuestGames(filteredGames));
    }
  });

  const onCloseButtonPress = () => {
    playTap(true);
    closeModal();
  };

  const launchEreader = async (bookId, chapterName = '') => {
    const gameLaunchedFrom = {
      location: 'ChallengeQuestPopup',
      categoryId: '',
      questId: 0,
    };
    const navigationParams = {
      destination: bookId,
      destinationGroup: DESTINATION_GROUP.EREADER,
      origin: NAVIGATION_ORIGIN.WORKSHOP,
      trigger: 'ChallengeQuestModal',
      triggerType: NAVIGATION_TRIGGER_TYPE.MANUAL,
    };
    dispatch(reportNavigation({ ...navigationParams }));
    dispatch(
      createActionObj(GameIds.METAGAME, 'SEARCHLIGHT_DISPATCH_EVENT', {
        gameId: GameIds.METAGAME,
        application: {
          gameId: GameIds.E_READER,
          eventType: 'APP_LAUNCHED',
          details: {
            questId: 0,
            isQuestWorkflow: true,
          },
        },
      }),
    );

    dispatch(
      openEreader({
        navigationParams,
        gameLaunchedFrom,
        isQuestWorkflow: true,
        params: {
          questId: 0,
          game: bookId,
          skillId: gameSelectedSkillId,
          location: 'ChallengeQuestPopup',
          isSideQuest: false,
          isQuestWorkflow: true,
          isChallengeQuest: true,
        },
        bookInfo: {
          bookGameId: bookId,
          chapter: chapterName,
        },
      }),
    );
  };

  const onGoButtonPress = () => {
    playTap();
    if (clickedRef.current) return;
    clickedRef.current = true;
    timeout.current = setTimeout(() => {
      closeModal();
      // start game
      const selectedGame = games.find(game => game.skillId === gameSelectedSkillId);
      if (selectedGame.type === STEP_TYPE.EREADER) {
        launchEreader(
          selectedGame.game,
          findChapterName(selectedGame.game, selectedGame.levels[0]),
        );
        return;
      }
      const gameLaunchedFrom = {
        location: 'Curioso',
        categoryId: '0',
        questId: 0,
      };
      dispatch(setGameLaunchedFrom(gameLaunchedFrom));
      const navigationParams = {
        destinationGroup: DESTINATION_GROUP.CHALLENGE_GAME,
        origin: NAVIGATION_ORIGIN.CHALLENGE_POPUP,
        trigger: 'challengeStartButton',
        triggerType: NAVIGATION_TRIGGER_TYPE.MANUAL,
      };
      dispatch(
        goToActivity({
          activityName: selectedGame.game,
          isQuestWorkflow: true,
          params: {
            isSideQuest: false,
            isQuestWorkflow: true,
            isChallengeQuest: true,
            questLevels: selectedGame.levels,
            questLevelsMastered: selectedGame.levelsMastered,
            location: undefined,
            game: selectedGame.game,
            questId: 0,
            skillId: selectedGame.skillId,
          },
          navigationParams,
        }),
      );
    }, 150);
  };

  const gridCollection = games.map(
    (
      { game: name, skillId, levelsMastered, levels, type, friendlyName },
    ) => {
      const gameId = getGameId({ name, type });
      return (
        <StyledIconWrapper key={gameId}>
          <GameIcon
            style={{ paddingBottom: '10px', position: 'relative', width: '100%', height: '100%' }}
            game={{
              canonicalName: gameId,
              displayName: getGameName({ name, type, friendlyName }),
              skillId,
            }}
            selectedGameSkillId={gameSelectedSkillId}
            isStarted={levelsMastered.length < levels.length}
            completed={levelsMastered.length >= levels.length}
            actionName={`workshop-challenge-quest-modal:${gameId}`}
            onSelectGame={(id) => {
              playTap(true);
              setGameSelectedSkillId(id);
            }}
          />
        </StyledIconWrapper>
      );
    },
  );

  const modalDescription = useMemo(() => {
    const gamesCount = gridCollection.length === 1 ? 'a new game' : 'some new games';
    return `Hello again, friend! I’ve selected ${gamesCount} for you to keep your reading skills sharp.`;
  }, [gridCollection.length]);

  return (
    <ModalContainer
      role="dialog"
      aria-modal="true"
      aria-labelledby="challenge-modal-title"
      data-testid="challenge-quest-modal"
      data-dd-action-name="workshop-challenge-quest-modal"
    >
      <CloseModalButton
        onClick={onCloseButtonPress}
        data-testid="challenge-quest-modal-close-button"
        data-dd-action-name="workshop-challenge-quest-modal:close"
      />
      <BackgroundContainer>
        <img src={ModalLargeBackground} alt="" />
      </BackgroundContainer>
      <IconContainer data-testid="challenge-quest-modal-icon">
        <img src={OliviaIcon} alt="Olivia" />
      </IconContainer>
      <TitleContainer>
        <Title id="challenge-modal-title">CHALLENGE</Title>
      </TitleContainer>
      <DescriptionContainer data-testid="challenge-quest-modal-description">
        <Description>
          {modalDescription}
        </Description>
      </DescriptionContainer>
      {isLoaded ? (
        <GamesContainer>{gridCollection}</GamesContainer>
      ) : (
        <Spinner style={{ top: 25 }} />
      )}

      <GoButton
        ariaLabel="Launch Selected Game"
        disabled={!gameSelectedSkillId}
        onClick={onGoButtonPress}
        actionName="workshop-challenge-quest-modal:go"
      />
    </ModalContainer>
  );
};

ChallengeQuestModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default ChallengeQuestModal;
